import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CourierUserApi, MailBankApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { login } from '../../state/authSlice';
import { AccessToken } from '@shared/sdk-v1/models';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { useTrackEvent } from '../../utils/track';

export const useEmailVerification = () => {
  const dispatch = useDispatch();
  const track = useTrackEvent();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  const sendEmailVerification = async (userId: string, newContact?: string) => {
    try {
      setIsSendingVerification(true);
      const mailApi = new MailBankApi(apiConfig());
      await mailApi.sendOtp({
        userId: userId,
        method: 'email',
        newContact,
      });
      showSuccessToast({
        title: 'Success',
        message:
          'Verification email sent. Please check your email (including your spam/junk folder) for the verification code.',
      });
    } catch (error) {
      showErrorToast({
        title: 'Error',
        message: 'Failed to send verification email',
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  const verifyEmailCode = async (
    code: string,
    userId: string,
    pendingAccessToken?: AccessToken
  ) => {
    try {
      setIsLoading(true);
      const authApi = new CourierUserApi(apiConfig());
      const response = await authApi.courierUserVerifyCode({
        code: code,
        userId: userId,
        method: 'email',
      });

      if (response.success) {
        track('email_verified');
        // Use the stored access token instead of re-logging in
        if (pendingAccessToken) {
          // console.log('Using pending access token for login');
          dispatch(login(pendingAccessToken));
        } else {
          // console.log(
          //   'No pending access token, verification successful but login will be handled separately'
          // );
        }
        return { success: true };
      } else {
        showErrorToast({
          title: 'Error',
          message: response.message || 'Failed to verify email',
        });
        return { success: false };
      }
    } catch (error) {
      console.error('Email verification error:', error);
      showErrorToast({
        title: 'Error',
        message: 'Failed to verify email. Please try again.',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSendingVerification,
    sendEmailVerification,
    verifyEmailCode,
  };
};
